import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Breadcrumb from '@/components/common/Breadcrumb';

import { fetchProjectDetail } from '@/service/api/project';
import {
  TermsTab,
  ContractTab,
  MetricsTab,
  DataTab,
  BillingTab,
  EpisodesTab,
  MaterialsTab,
  TimelineTab,
  formatMoney,
  formatDate,
  formatNumber,
  getStatusConfig
} from '../modules';

import { getRevenueStatusInfo } from '@/utils/revenue';

type ProjectTab = 'terms' | 'episodes' | 'materials' | 'contract' | 'metrics' | 'data' | 'billing';

const tabs: { key: ProjectTab; label: string; icon: string }[] = [
  { key: 'terms', label: '合作条款', icon: 'ri-file-text-line' },
  { key: 'episodes', label: '剧集交付', icon: 'ri-clapperboard-line' },
  { key: 'materials', label: '申请与资料', icon: 'ri-folder-info-line' },
  { key: 'contract', label: '合同归档', icon: 'ri-file-copy-2-line' },
  { key: 'metrics', label: '运营数据', icon: 'ri-bar-chart-grouped-line' },
  { key: 'data', label: '周期数据', icon: 'ri-bar-chart-2-line' },
  { key: 'billing', label: '收益账单', icon: 'ri-money-cny-circle-line' },
];

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<ProjectTab>('terms');
  const [project, setProject] = useState<Api.Project.ProjectDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const hasFetchedRef = useRef(false);

  const loadProjectDetail = useCallback(async (force = false) => {
    if (!id) return;
    if (hasFetchedRef.current && !force) return;
    hasFetchedRef.current = true;

    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await fetchProjectDetail(id);
      setProject(data || null);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '获取项目详情失败');
      hasFetchedRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProjectDetail();
  }, [loadProjectDetail]);

  const dashboard = project?.operation_dashboard;
  const metrics = dashboard?.metrics;
  const financialTotals = dashboard?.financial_totals || [];
  const statementStatusCounts = dashboard?.statement_status_counts || [];
  const settlementStatusCounts = dashboard?.settlement_status_counts || [];

  const statusConfig = getStatusConfig(project?.status);

  /**
   * 渲染后端返回的累计分成收益 (直接取后端返回的 financial_totals[].owner_share_amount，绝不前端自算累加)
   */
  const renderOwnerShareTotals = (totals?: Api.Revenue.CurrencyFinancialTotalsResponse[]) => {
    if (!totals || totals.length === 0) return '-';
    return totals
      .map((item) => formatMoney(item.owner_share_amount, item.currency))
      .join(' / ');
  };

  /** 状态徽章映射 (参照公共收益状态枚举) */
  const renderStatusBadge = (status?: string) => {
    const item = getRevenueStatusInfo(status);

    return (
      <span className={`text-xs px-2 py-0.5 rounded-md border font-medium ${item.color}`}>
        {item.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <div className="pt-16 sm:pt-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '发行项目', href: '/projects' },
              { label: project?.project_no || id },
            ]}
            className="mb-2"
          />

          {/* Loading State */}
          {isLoading && (
            <div className="py-20 text-center">
              <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-foreground-500">正在加载项目详情...</p>
            </div>
          )}

          {/* Error State */}
          {!isLoading && errorMessage && (
            <div className="glass-card rounded-2xl p-12 text-center my-8">
              <i className="ri-error-warning-line text-4xl text-red-500 mb-3 block"></i>
              <h3 className="text-lg font-bold text-foreground-900 mb-2">获取项目数据失败</h3>
              <p className="text-sm text-foreground-500 mb-6">{errorMessage}</p>
              <button
                onClick={() => loadProjectDetail(true)}
                className="px-5 py-2.5 rounded-xl bg-primary-500 text-foreground-950 font-medium text-sm hover:bg-primary-400 transition-all cursor-pointer shadow-sm"
              >
                重试加载
              </button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !errorMessage && !project && (
            <div className="glass-card rounded-2xl p-12 text-center my-8">
              <i className="ri-inbox-archive-line text-4xl text-foreground-300 mb-3 block"></i>
              <h3 className="text-lg font-bold text-foreground-900 mb-2">未找到该项目信息</h3>
              <p className="text-sm text-foreground-500 mb-6">该项目可能已被删除或无访问权限</p>
              <Link
                to="/projects"
                className="px-5 py-2.5 rounded-xl bg-primary-500 text-foreground-950 font-medium text-sm hover:bg-primary-400 transition-all inline-block cursor-pointer"
              >
                返回项目列表
              </Link>
            </div>
          )}

          {/* Project Content */}
          {!isLoading && !errorMessage && project && (
            <>
              {/* Project Header */}
              <div className="glass-card rounded-2xl p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-foreground-400 font-mono">{project.project_no || project.id}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <h1 className="font-heading font-bold text-xl text-foreground-950">
                      {project.title || project.source_application?.title || '未命名项目'}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-foreground-500">
                      <span>{project.total_episode_count || project.source_application?.total_episode_count || 0} 集</span>
                      <span>签约于 {formatDate(project.created_at || project.contract?.created_at)}</span>
                      {project.owner_subject_name && (
                        <span>主体：{project.owner_subject_name}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Channel Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {project.channels && project.channels.length > 0 ? (
                    project.channels.map((ch, idx) => (
                      <span key={ch.id || idx} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-foreground-600 border border-gray-200/60">
                        {ch.channel?.name || '默认渠道'}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-foreground-400 italic">暂无分配渠道</span>
                  )}
                </div>
              </div>

              {/* 上方左右响应式布局：左侧看板 (2/3) + 右侧进度时间线 (1/3) */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* 左侧：项目运营与收益看板 (lg:col-span-2) */}
                <div className="lg:col-span-2 glass-card rounded-2xl p-6 border border-primary-100/60 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-5 pb-3 border-b border-background-100">
                      <div className="flex items-center gap-2">
                        <i className="ri-dashboard-3-line text-primary-600 text-lg"></i>
                        <h2 className="font-bold text-foreground-950 text-base">项目运营与收益看板</h2>
                      </div>
                      <button
                        onClick={() => loadProjectDetail(true)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-background-300/60 text-foreground-600 hover:bg-gray-50 transition-all flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-refresh-line"></i>
                        刷新看板
                      </button>
                    </div>

                    {/* Summary Metrics Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                      <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200/60 text-center">
                        <p className="text-lg font-bold text-foreground-950">{formatNumber(metrics?.play_count)}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">播放量</p>
                      </div>
                      <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200/60 text-center">
                        <p className="text-lg font-bold text-foreground-950">{formatNumber(metrics?.like_count)}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">点赞数</p>
                      </div>
                      <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200/60 text-center">
                        <p className="text-lg font-bold text-foreground-950">{formatNumber(metrics?.comment_count)}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">评论数</p>
                      </div>
                      <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200/60 text-center">
                        <p className="text-lg font-bold text-foreground-950">{formatNumber(metrics?.favorite_count)}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">收藏数</p>
                      </div>
                      <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200/60 text-center">
                        <p className="text-lg font-bold text-foreground-950">{formatNumber(metrics?.conversion_count)}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">转化数</p>
                      </div>
                    </div>

                    {/* 财务总计面板 (Financial Totals) */}
                    {financialTotals.length > 0 && (
                      <div className="mb-6 p-4 rounded-xl bg-white border border-background-200/70">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-background-100">
                          <span className="text-xs font-bold text-foreground-900 flex items-center gap-1">
                            <i className="ri-money-cny-box-line text-primary-600"></i> 财务总计面板 (Financial Totals)
                          </span>
                          <span className="text-[11px] text-foreground-400">各币种收益与分成汇总</span>
                        </div>

                        <div className="space-y-3">
                          {financialTotals.map((item, idx) => (
                            <div key={item.currency || idx} className="p-3.5 rounded-lg bg-background-50/60 border border-background-200/50">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-primary-100 text-primary-800 uppercase">
                                  {item.currency || 'USD'} 结算汇总
                                </span>
                                <span className="text-xs text-foreground-500">
                                  可分配金额: <strong className="text-foreground-900 font-semibold">{formatMoney(item.distributable_amount, item.currency)}</strong>
                                </span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                <div className="p-2 bg-white rounded border border-background-200/50">
                                  <p className="text-foreground-400 text-[11px]">渠道总收入</p>
                                  <p className="font-semibold text-foreground-900 mt-0.5">{formatMoney(item.gross_income_amount, item.currency)}</p>
                                </div>
                                <div className="p-2 bg-white rounded border border-background-200/50">
                                  <p className="text-foreground-400 text-[11px]">渠道扣除金额</p>
                                  <p className="font-semibold text-rose-600 mt-0.5">
                                    -{formatMoney(item.channel_deduction_amount, item.currency)}
                                  </p>
                                </div>
                                <div className="p-2 bg-white rounded border border-background-200/50">
                                  <p className="text-foreground-400 text-[11px]">人工扣除金额</p>
                                  <p className="font-semibold text-rose-600 mt-0.5">
                                    -{formatMoney(item.manual_deduction_amount, item.currency)}
                                  </p>
                                </div>
                                <div className="p-2 bg-white rounded border border-background-200/50">
                                  <p className="text-foreground-400 text-[11px]">内容方分成金额</p>
                                  <p className="font-semibold text-emerald-600 mt-0.5">{formatMoney(item.owner_share_amount, item.currency)}</p>
                                </div>
                              </div>
                              <div className="mt-2.5 pt-2 border-t border-background-200/40 flex flex-wrap items-center justify-between text-[11px] text-foreground-500 gap-2">
                                <span>平台分成: <strong className="text-primary-700 font-medium">{formatMoney(item.platform_share_amount, item.currency)}</strong></span>
                                <span>申请结算: <strong className="text-foreground-800">{formatMoney(item.settlement_requested_amount, item.currency)}</strong></span>
                                <span>待打款: <strong className="text-amber-600 font-medium">{formatMoney(item.pending_payment_amount, item.currency)}</strong></span>
                                <span>已打款: <strong className="text-teal-600 font-medium">{formatMoney(item.paid_amount, item.currency)}</strong></span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 收益账单状态面板 */}
                    <div className="p-4 rounded-xl bg-white border border-background-200/70">
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-background-100">
                        <span className="text-xs font-bold text-foreground-900 flex items-center gap-1">
                          <i className="ri-file-list-3-line text-blue-600"></i> 收益账单状态统计
                        </span>
                      </div>
                      {statementStatusCounts.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {statementStatusCounts.map((sc, i) => (
                            <div key={sc.status || i} className="flex items-center justify-between p-2 bg-background-50/70 rounded border border-background-100">
                              <div>{renderStatusBadge(sc.status)}</div>
                              <span className="font-bold text-foreground-900">{sc.count ?? 0} <span className="text-[11px] font-normal text-foreground-400">单</span></span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-foreground-400 text-center py-2">暂无账单状态统计</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 右侧：进度时间线区块 (lg:col-span-1) */}
                <div className="lg:col-span-1 glass-card rounded-2xl p-6 border border-gray-200/70 h-full flex flex-col justify-between">
                  <TimelineTab project={project} />
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex items-center gap-1 mb-8 overflow-x-auto border-b border-background-200/70 pb-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-t-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer border-b-2 -mb-[3px] ${activeTab === tab.key
                      ? 'text-primary-600 border-primary-500'
                      : 'text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-gray-50/50'
                      }`}
                  >
                    <i className={`${tab.icon} text-base`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="glass-card rounded-2xl p-6 md:p-8">
                {activeTab === 'terms' && <TermsTab project={project} />}
                {activeTab === 'episodes' && <EpisodesTab project={project} />}
                {activeTab === 'materials' && <MaterialsTab project={project} />}
                {activeTab === 'contract' && <ContractTab project={project} />}
                {activeTab === 'metrics' && <MetricsTab project={project} />}
                {activeTab === 'data' && <DataTab project={project} onRefresh={() => loadProjectDetail(true)} />}
                {activeTab === 'billing' && <BillingTab project={project} onRefresh={() => loadProjectDetail(true)} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}